import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { Menu, Home, Settings, Shield, TrendingUp, Activity, CalendarDays, Receipt, BellRing, Package } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Separator } from '@/components/ui/separator'
import { useAuth } from '@/lib/auth-context'
import { PERMISSIONS } from '@/lib/permissions'

const mainNavItems = [
  { icon: Home, label: 'Accueil', path: '/home' },
]

const secondaryNavItems: Array<{
  icon: LucideIcon
  label: string
  path: string
  requiredPermissions?: string[]
  requiredFeature?: 'planning'
}> = [
  { icon: Receipt, label: 'Factures', path: '/invoices', requiredPermissions: [PERMISSIONS.INVOICE_WRITE] },
  { icon: CalendarDays, label: 'Planning', path: '/planning', requiredPermissions: [PERMISSIONS.PLANNING_READ], requiredFeature: 'planning' },
  { icon: Package, label: 'Commandes fournisseurs', path: '/supplier-orders', requiredPermissions: [PERMISSIONS.ORDERS_READ], requiredFeature: 'planning' },
  { icon: TrendingUp, label: 'Analytiques', path: '/business', requiredPermissions: [PERMISSIONS.SETTINGS_MANAGE] },
  { icon: Activity, label: 'Activité', path: '/activity', requiredPermissions: [PERMISSIONS.SETTINGS_MANAGE] },
  { icon: BellRing, label: 'Alertes du suivi', path: '/admin/alertes', requiredPermissions: [PERMISSIONS.SETTINGS_MANAGE], requiredFeature: 'planning' },
  { icon: Shield, label: 'Équipe', path: '/admin', requiredPermissions: [PERMISSIONS.USERS_MANAGE] },
  { icon: Settings, label: 'Paramètres', path: '/settings', requiredPermissions: [PERMISSIONS.SETTINGS_MANAGE] },
]

export function MenuSheet() {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const pathname = location.pathname
  const { user } = useAuth()

  const hasAllPermissions = (required: string[] | undefined) => {
    if (!required || required.length === 0) return true
    const perms = user?.permissions || []
    return required.every((p) => perms.includes(p))
  }

  const hasFeature = (feature: 'planning' | undefined) => !feature || user?.features?.[feature] === true

  const visibleSecondaryItems = secondaryNavItems.filter(
    (it) => hasAllPermissions(it.requiredPermissions) && hasFeature(it.requiredFeature),
  )

  const handleNavigation = () => {
    setOpen(false)
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Ouvrir le menu">
          <Menu className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px]">
        <SheetHeader>
          <SheetTitle>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-primary">SOLY</span>
            </div>
          </SheetTitle>
        </SheetHeader>
        
        <nav className="flex flex-col gap-2 py-6">
          {mainNavItems.map((item) => {
            const Icon = item.icon
            const isActive = pathname === item.path || pathname?.startsWith(item.path + '/')
            
            return (
              <Link key={item.path} to={item.path} onClick={handleNavigation}>
                <Button
                  variant={isActive ? 'secondary' : 'ghost'}
                  className="w-full justify-start gap-2"
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Button>
              </Link>
            )
          })}
          
          <Separator className="my-4" />
          
          {visibleSecondaryItems.map((item) => {
            const Icon = item.icon
            const isActive = pathname === item.path || pathname?.startsWith(item.path + '/')
            
            return (
              <Link key={item.path} to={item.path} onClick={handleNavigation}>
                <Button
                  variant={isActive ? 'secondary' : 'ghost'}
                  className="w-full justify-start gap-2"
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Button>
              </Link>
            )
          })}
        </nav>
      </SheetContent>
    </Sheet>
  )
}
